import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as yup from "yup";
import iconAddPhoto from "../../assets/icon-camera.svg";
import iconHeart from "../../assets/Vector (23).png";

import { AuthContext } from "../../providers/AuthProvider";
import editIcon from "../../assets/icon-profile-photo.svg";
import unmask from "../../utils/unmask";
import api from "../../utils/api";

import "./ClientProfile.css";
import { AuthorizedLayout } from "../../components/AuthorizedLayout";
import { InputAutoComplete } from "../../components/InputAutoComplete/InputAutoComplete";
import { TextInput } from "../../components/inputs/TextInput/TextInput";
import { TextArea } from "../../components/inputs/TextArea/TextArea";
import { SelectInput } from "../../components/inputs/SelectInput/SelectInput";
import { MultiSelectInput } from "../../components/inputs/MultiSelectInput/MultiSelectInput";
import PhoneInput from "../../components/inputs/PhoneInput/PhoneInput";
import { CompleteProfileCard } from "../../components/CompleteProfileCard/CompleteProfileCard";

import axios from "axios";
import { getBase64 } from '../../utils/getBase64';

const ClientProfile = () => {
  const { updateUser, user, signOut } = useContext(AuthContext);
  const [saveLoading, setSaveLoading] = useState(false);
  const [brazilStates, setBrazilStates] = useState([]);
  const [cities, setCities] = useState([]);

  const form = useFormik({
    initialValues: {
      name: user?.name || "",
      birthday: user?.birthdate,
      email: user?.email || "",
      phone: user?.phone || "",
      avatar: user?.avatar || null,
      state: user?.state || "",
      city: user?.city || "",
      linkedin: user?.linkedinUrl || "",
      salary: user?.salary
        ? (Number(user.salary) || 0).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })
        : "",
      salaryNegotiable: user?.salaryNegotiable,
      contractType: user?.contractType === "clt" ? 0
        : user?.contractType === "pj" ? 1
          : user?.contractType === "both" ? 2
            : null,
      workModel: Array.isArray(user?.workModel) ? user.workModel : [],
      seniorityLevel: user?.seniorityLevel || null,
      bio: user?.bio || "",
    },

    validationSchema: yup.object({
      name: yup
        .string()
        .required("Nome completo é obrigatório")
        .test('full-name', 'Digite seu nome completo (nome e sobrenome)', (value) => {
          if (!value) return false;
          const words = value.trim().split(/\s+/);
          return words.length >= 2 && words.every(word => word.length > 0);
        }),
      email: yup
        .string()
        .email("E-mail inválido")
        .required("E-mail é obrigatório"),
      phone: yup
        .string()
        .required("Telefone é obrigatório"),
      state: yup
        .string()
        .required("Estado é obrigatório"),
      city: yup
        .string()
        .required("Cidade é obrigatória"),
      linkedin: yup
        .string()
        .url("Deve ser uma URL válida"),
      // .required("LinkedIn é obrigatório"),
      salary: yup
        .string()
        .required("Pretensão salarial é obrigatória")
        .test(
          "greater-than-zero",
          "A pretensão salarial deve ser maior que R$ 0,00",
          (value) => {
            const numeric = Number((value || "").replace(/\D/g, "")) / 100;
            return numeric > 0;
          }
        ),
      salaryNegotiable: yup
        .boolean()
        .required("Escolha se está aberto à negociação salarial"),
      contractType: yup
        .number()
        .typeError("Regime de contratação é obrigatório")
        .required("Regime de contratação é obrigatório"),
      workModel: yup
        .array()
        .min(1, "Selecione pelo menos uma opção")
        .required("Modelo de trabalho é obrigatório"),
      birthday: yup
        .string()
        .required("Data de nascimento é obrigatória"),
      bio: yup
        .string()
        .min(200, "Mini bio deve ter no mínimo 200 caracteres")
        .max(450, "Mini bio deve ter no máximo 450 caracteres")
        .required("Mini bio é obrigatória"),
    }),

    onSubmit: (values) => updateSettings(values),
  });

  const resetForm = (values) => {
    form.setValues({
      name: values?.name || "",
      birthday: values?.birthdate,
      email: values?.email || "",
      phone: values?.phone || "",
      avatar: values?.avatar || null,
      state: values?.state || "",
      city: values?.city || "",
      linkedin: values?.linkedinUrl || "",
      salary: values?.salary
        ? (Number(values.salary) || 0).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })
        : "",
      salaryNegotiable: values?.salaryNegotiable,
      contractType: values?.contractType === "clt" ? 0
        : values?.contractType === "pj" ? 1
          : values?.contractType === "both" ? 2
            : null,
      workModel: Array.isArray(values?.workModel) ? values.workModel : [],
      seniorityLevel: values?.seniorityLevel || null,
      bio: values?.bio || "",
    });
  };

  const [avatarSrc, setAvatarSrc] = useState(user?.avatar || editIcon);

  const handleImageError = () => {
    setAvatarSrc(user?.avatar);
  };

  useEffect(() => {
    if (user.avatar) {
      handleImageError();
    }
  }, [user]);

  useEffect(() => {
    if (user.state && brazilStates.length > 0) {
      getCities(user.state);
    }
  }, [user.state, brazilStates]);

  const updateSettings = async (values) => {
    setSaveLoading(true);

    if (values?.avatar !== user?.avatar && !values.avatar) {
      try {
        const { data } = await api.put(`/talents/talents/${user.id}`, {
          data: {
            attributes: {
              avatar: { data: values?.avatar },
              name: values.name.trim(),
              phone: unmask(values.phone.trim()),
              birthdate: values.birthday,
              email: values.email,
              state: values.state,
              city: values.city,
              linkedin_url: values.linkedin,
              salary: values.salary
                ? Number(values.salary.replace(/\D/g, "")) / 100
                : null,
              salary_negotiable: values.salaryNegotiable,
              contract_type:
                values.contractType !== "" ? parseInt(values.contractType, 10) : null,
              work_model: values.workModel || [],
              seniority_level: values.seniorityLevel,
              bio: values.bio,
            },
          },
        });
        if (data) {
          updateUser(data);
          resetForm(data.data.attributes);
          toast.success("Dados da conta atualizados com sucesso!");
        }
      } catch (error) {
        toast.error("Ocorreu um erro ao tentar atualizar os dados da conta.");
      } finally {
        setSaveLoading(false);
      }
    } else {
      try {
        const { data } = await api.put(`/talents/talents/${user.id}`, {
          data: {
            attributes: {
              name: values.name.trim(),
              phone: unmask(values.phone.trim()),
              birthdate: values.birthday,
              email: values.email,
              state: values.state,
              city: values.city,
              linkedin_url: values.linkedin,
              salary: values.salary
                ? Number(values.salary.replace(/\D/g, "")) / 100
                : null,
              salary_negotiable: values.salaryNegotiable,
              contract_type:
                values.contractType !== "" ? parseInt(values.contractType, 10) : null,
              work_model: values.workModel || [],
              seniority_level: values.seniorityLevel,
              bio: values.bio,
            },
          },
        });
        if (data) {
          updateUser(data);
          resetForm(data.data.attributes);
          toast.success("Dados da conta atualizados com sucesso!");
        }
      } catch (error) {
        toast.error("Ocorreu um erro ao tentar atualizar os dados da conta.");
      } finally {
        setSaveLoading(false);
      }
    }
  };

  const handleFileInputChange = async (e) => {
    setSaveLoading(true);
    const file = e.target.files[0];

    getBase64(file, 350, 350) // Reduzindo a imagem para 500x500 (ou o tamanho que preferir)
      .then((result) => {
        file["base64"] = result;
        form.setFieldValue(`avatar`, result); // Atualiza o campo do formulário
        setAvatarSrc(result); // Atualiza o estado da imagem

        const updateSettings = async () => {
          try {
            const { data } = await api.put(`/talents/talents/${user.id}`, {
              data: {
                attributes: {
                  avatar: { data: result },
                },
              },
            });

            if (data) {
              updateUser(data); // Atualiza o usuário
              resetForm(data.data.attributes); // Reseta o formulário
              toast.success("Foto do perfil atualizada com sucesso!");
            }
          } catch (error) {
            toast.error(
              "Ocorreu um erro ao tentar atualizar os dados da conta."
            );
          } finally {
            setSaveLoading(false);
          }
        };

        updateSettings();
      })
      .catch((error) => {
        if (error?.response?.status) {
          if (error.response.status !== 401) {
            if (
              error?.response?.data?.errors &&
              error?.response?.data?.errors[0]?.title
            ) {
              alert(`${error.response.data.errors[0].title}`);
            } else {
              alert("Something unexpected happened. Please try again.");
            }
          } else {
            alert("Session expired", "Please login in again.");
          }
        } else {
          alert("Something unexpected happened. Please try again.");
        }
      });
  };

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const { data } = await axios.get(
          "https://servicodados.ibge.gov.br/api/v1/localidades/estados"
        );
        setBrazilStates(data);
      } catch (error) {
        console.error("Erro ao carregar estados:", error);
      }
    };

    fetchStates();
  }, []);

  const getCities = async (name) => {
    try {
      const stateFound = brazilStates.find(state => state.sigla === name)

      const data = await axios.get(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateFound.id}/municipios`
      );
      if (data) {
        setCities(data?.data);
      }
    } catch (error) { }
  };

  return (
    <>
      <AuthorizedLayout>
        <div className="containerProfileTalents">
          <div
            style={{
              width: "100%",
              height: "100%",
            }}
          >
            <div className="containerContentProfile">
              <CompleteProfileCard showButton={false} />

              <div className="formProfile">
                <div className="containerPhotoProfile">
                  <label className="uploadIcon" htmlFor="fupload">
                    <div style={{ position: "relative" }}>
                      {user.avatar && (
                        <img
                          src={iconAddPhoto}
                          alt="edit"
                          style={{
                            position: "absolute",
                            bottom: "-10px",
                            right: "5px",
                            width: "40px",
                            height: "40px",
                          }}
                        />
                      )}
                      <img
                        src={avatarSrc}
                        alt="edit"
                        className={form.values.avatar ? "fotoIcon" : "editIcon"}
                        htmlFor="fupload"
                        onError={handleImageError}
                      />
                    </div>

                    <input
                      type="file"
                      id="fupload"
                      name="fupload"
                      className="fupload form-control"
                      accept=".jpeg, .png, .jpg"
                      onChange={handleFileInputChange}
                      style={{ display: "none" }}
                    />
                  </label>
                </div>

                <div className="containerInput">
                  <TextInput
                    name="name"
                    label="Nome completo"
                    value={form.values.name}
                    onChange={(val) => form.setFieldValue("name", val)}
                    placeholder="Nome completo"
                    error={form.errors.name}
                  />
                </div>

                <div className="containerInput">
                  <TextInput
                    name="birthday"
                    label="Data de nascimento"
                    placeholder="Data de nascimento"
                    maskType="date"
                    value={form.values.birthday ? form.values.birthday : ""}
                    onChange={(val) => {
                      if (val) {
                        form.setFieldValue("birthday", val);
                      } else {
                        form.setFieldValue("birthday", "");
                      }
                    }}
                    error={form.errors.birthday}
                  />
                </div>

                <div className="containerInput">
                  <TextInput
                    type="email"
                    name="email"
                    label="E-mail"
                    placeholder="E-mail"
                    value={form.values.email}
                    onChange={(val) => form.setFieldValue("email", val)}
                    onBlur={(val) => form.handleBlur("email", val)}
                    error={form.errors.email}
                  />
                </div>

                <div className="containerInput">
                  <PhoneInput
                    name="phone"
                    label="Telefone"
                    placeholder="Telefone"
                    value={form.values.phone}
                    onChange={(val) => form.setFieldValue("phone", val)}
                    error={form.errors.phone}
                  />
                </div>

                <div className="containerInput">
                  <InputAutoComplete
                    label="Estado"
                    placeholder="Selecione seu estado"
                    options={brazilStates || []}
                    value={
                      brazilStates?.find((state) => {
                        return state.sigla === (form.values.state || user?.state || "");
                      }) || null
                    }
                    getOptionLabel={(option) => option.nome || ""}
                    onChange={(event, newValue) => {
                      const selectedValue =
                        newValue && newValue.sigla ? newValue.sigla.trim() : "";
                      form.setFieldValue("state", selectedValue);
                      getCities(selectedValue);
                    }}
                  />
                  {form.touched.state && form.errors.state && (
                    <div className="textError">
                      {form.errors.state}
                    </div>
                  )}
                </div>

                <div className="containerInput">
                  <InputAutoComplete
                    label="Cidade"
                    placeholder="Digite sua cidade"
                    options={cities || []}
                    value={
                      cities?.find((city) => {
                        const cleanText = (str) => {
                          if (!str) return "";
                          return str
                            .toString()
                            .normalize("NFD")
                            .replace(/[\u0300-\u036f]/g, "")
                            .toLowerCase()
                            .replace(/[.,\-\s]/g, "")
                            .trim();
                        };

                        return city && city.nome
                          ? cleanText(city.nome) === cleanText(form.values.city || user?.city || "")
                          : false;
                      }) || null
                    }
                    getOptionLabel={(option) => option.nome || ""}
                    onChange={(event, newValue) => {
                      const selectedValue = newValue?.nome?.trim() || "";
                      form.setFieldValue("city", selectedValue);
                    }}
                  />
                  {form.touched.city && form.errors.city && (
                    <div className="textError">
                      {form.errors.city}
                    </div>
                  )}
                </div>

                <div className="containerInput">
                  <TextInput
                    name="linkedin"
                    label="LinkedIn"
                    placeholder="https://linkedin.com/in/usuario"
                    value={form.values.linkedin}
                    onChange={(val) => {
                      const input = val.trim();
                      const prefix = "https://www.linkedin.com/in/";
                      const cleaned = input.replace(/(https:\/\/)?(www\.)?linkedin\.com\/in\//gi, "").replace(/^\/+/, "");
                      form.setFieldValue("linkedin", cleaned ? `${prefix}${cleaned}` : "");
                    }}
                    onBlur={(val) => form.handleBlur("linkedin", val)}
                    error={form.errors.linkedin}
                  />
                </div>

                <div className="containerInput">
                  <SelectInput
                    name="contractType"
                    label="Regime de Contratação"
                    placeholder="Selecione uma opção"
                    value={form.values.contractType !== undefined && form.values.contractType !== null ? String(form.values.contractType) : ""}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      form.setFieldValue("contractType", value);
                    }}
                    options={[
                      { label: "CLT", value: "0" },
                      { label: "PJ", value: "1" },
                      { label: "Ambos", value: "2" },
                    ]}
                    error={form.touched.contractType && form.errors.contractType}
                  />
                </div>

                <div className="containerInput">
                  <MultiSelectInput
                    name="workModel"
                    label="Modelo de Trabalho"
                    value={Array.isArray(form.values.workModel) ? form.values.workModel : []}
                    onChange={(values) => {
                      form.setFieldValue("workModel", values);
                    }}
                    options={[
                      { label: "Presencial", value: "in_person" },
                      { label: "Home Office", value: "remote" },
                      { label: "Híbrido", value: "hybrid" },
                    ]}
                    placeholder="Selecione pelo menos uma opção"
                    error={form.touched.workModel && form.errors.workModel}
                  />
                </div>

                <div className="containerInput">
                  <SelectInput
                    name="seniorityLevel"
                    label="Nível de Senioridade"
                    placeholder="Selecione uma opção"
                    value={form.values.seniorityLevel !== undefined && form.values.seniorityLevel !== null ? String(form.values.seniorityLevel) : ""}
                    onChange={(e) => {
                      form.setFieldValue("seniorityLevel", e.target.value);
                    }}
                    options={[
                      { label: 'Auxiliar', value: 'assistant' },
                      { label: 'Assistente', value: 'associate' },
                      { label: 'Analista Jr.', value: 'junior_analyst' },
                      { label: 'Analista Pleno', value: 'analyst' },
                      { label: 'Analista Sênior', value: 'senior_analyst' },
                      { label: 'Coordenador', value: 'coordinator' },
                      { label: 'Gerente', value: 'manager' },
                      { label: 'Diretor', value: 'director' },
                      { label: 'C-Level', value: 'c_level' }
                    ]}
                    error={form.touched.seniorityLevel && form.errors.seniorityLevel}
                  />
                </div>

                <div className="containerInput">
                  <TextInput
                    name="salary"
                    label="Pretensão Salarial"
                    placeholder="Pretensão Salarial"
                    value={form.values.salary}
                    onChange={(val) => {
                      const raw = val.replace(/\D/g, "");
                      const numberValue = Number(raw) / 100;
                      const formatted = numberValue.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      });
                      form.setFieldValue("salary", formatted);
                    }}
                    onBlur={(val) => {
                      form.handleBlur(`salary`, val);
                    }}
                    error={form.errors.salary}
                    maskType="currency"
                  />
                </div>

                <div className="containerInput">
                  <SelectInput
                    name="salaryNegotiable"
                    label="Aberto à negociação salarial"
                    placeholder="Selecione uma opção"
                    value={
                      form.values.salaryNegotiable === true
                        ? "true"
                        : form.values.salaryNegotiable === false
                          ? "false"
                          : ""
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      form.setFieldValue("salaryNegotiable", value === "true" ? true : value === "false" ? false : null);
                    }}
                    options={[
                      { label: "Sim", value: "true" },
                      { label: "Não", value: "false" },
                    ]}
                    error={form.touched.salaryNegotiable && form.errors.salaryNegotiable}
                  />
                </div>

                <div className="containerInput">
                  <TextArea
                    name="bio"
                    label="Mini bio"
                    placeholder="Uma breve apresentação sua"
                    value={form.values.bio}
                    onChange={(val) => {
                      form.setFieldValue("bio", val);
                    }}
                    onBlur={(val) => form.handleBlur("bio", val)}
                    minLength={200}
                    maxLength={450}
                    error={form.errors.bio}
                  />
                </div>

                <div
                  className={`buttonClientProfile ${saveLoading && "loading"}`}
                  onClick={form.submitForm}
                >
                  {saveLoading ? "Salvando" : "Salvar Alterações"}
                </div>
              </div>

              <div
                onClick={() => signOut()}
                className="buttonSiginOut"
              >
                Sair
              </div>

            </div>
          </div>
          <div className="footerMobile">
            Desenvolvido com
            <img src={iconHeart} alt="" />
            <strong>© eComLovers®</strong>
          </div>
        </div>
      </AuthorizedLayout>

      {saveLoading && (
        <div className="globalSpinnerOverlay">
          <div className="spinner" />
        </div>
      )}
    </>
  );
};

export default ClientProfile;
